/**
 * QuadrantDrillDown - Expandable panel showing OE element breakdown for a specific REPS quadrant.
 * Displays bar chart and card layout of the 5 OE element scores for the selected quadrant.
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Grid,
  LinearProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { REPS_COLORS, OE_ELEMENTS } from '../utils/constants';
import { getSeverity, getScoreColor } from '../utils/scoring';
import { thresholdFor } from '../utils/bands';

const QUADRANT_KEY_MAP = {
  reliability: 'R',
  efficiency: 'E',
  productivity: 'P',
  safety: 'S',
};

const SEVERITY_LABELS = {
  healthy: 'Healthy',
  monitor: 'Monitor',
  warning: 'Warning',
};

export default function QuadrantDrillDown({ quadrantKey, quadrantLabel, elementScores, thresholds, onClose }) {
  const repsKey = QUADRANT_KEY_MAP[quadrantKey];
  const threshold = thresholdFor(thresholds, quadrantKey);
  const color = REPS_COLORS[quadrantKey] || '#757575';

  // Extract scores for this quadrant from each OE element
  const elementData = OE_ELEMENTS.map((element) => {
    const scores = elementScores?.[element.key];
    const score = scores?.[repsKey] ?? null;
    return {
      ...element,
      score,
      severity: score != null ? getSeverity(score, threshold) : null,
      severityColor: score != null ? getScoreColor(score, threshold) : '#e0e0e0',
    };
  });

  const hasData = elementData.some((d) => d.score != null);

  return (
    <Collapse in={!!quadrantKey} timeout={400} unmountOnExit>
      <Card
        sx={{
          mb: 4,
          borderTop: `4px solid ${color}`,
          animation: 'slideDown 0.3s ease-out',
          '@keyframes slideDown': {
            from: { opacity: 0, transform: 'translateY(-10px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: color,
                }}
              />
              <Typography variant="h6">
                {quadrantLabel} — OE Element Breakdown
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" aria-label="Close drill-down">
              <CloseIcon />
            </IconButton>
          </Box>

          {!hasData ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No element score data available for {quadrantLabel}.
                Complete surveys to see the breakdown.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Bar chart layout */}
              <Box sx={{ mb: 3 }}>
                {elementData.map((element) => (
                  <Box key={element.key} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
                        {element.label}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {element.score != null && (
                          <Tooltip title={`${SEVERITY_LABELS[element.severity] || 'N/A'}`}>
                            <Chip
                              label={SEVERITY_LABELS[element.severity] || 'N/A'}
                              size="small"
                              sx={{
                                bgcolor: `${element.severityColor}15`,
                                color: element.severityColor,
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                height: 20,
                              }}
                            />
                          </Tooltip>
                        )}
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            fontFamily: '"Roboto Mono", monospace',
                            color: element.severityColor,
                            minWidth: 32,
                            textAlign: 'right',
                          }}
                        >
                          {element.score != null ? element.score.toFixed(1) : '—'}
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={element.score != null ? Math.min((element.score / 10) * 100, 100) : 0}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 5,
                          bgcolor: element.score != null ? element.severityColor : '#e0e0e0',
                          transition: 'transform 0.6s ease-out',
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Score cards grid */}
              <Grid container spacing={2}>
                {elementData.map((element) => (
                  <Grid item xs={6} sm={4} md={2.4} key={element.key}>
                    <Card
                      variant="outlined"
                      sx={{
                        textAlign: 'center',
                        py: 1.5,
                        borderColor: element.score != null ? `${element.severityColor}40` : 'divider',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontFamily: '"Roboto Mono", monospace',
                          fontWeight: 700,
                          color: element.severityColor,
                        }}
                      >
                        {element.score != null ? element.score.toFixed(1) : '—'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          fontSize: '0.65rem',
                          lineHeight: 1.2,
                          px: 0.5,
                        }}
                      >
                        {element.label}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </CardContent>
      </Card>
    </Collapse>
  );
}
